/**
 * Text-to-speech for the accessibility toolbar (Web Speech API).
 *
 * Not a WCAG requirement — assistive tech belongs to the user — but a useful
 * extra for readers with dyslexia or low vision, which is the audience named
 * in the school's accessibility statement.
 *
 * Reads the main content block by block, highlighting the block in progress.
 */

// Chrome drops utterances longer than ~15s, so we split long blocks at
// sentence boundaries and queue small chunks instead.
const MAX_CHUNK = 220

// Block-level elements worth reading, in document order.
const READABLE = 'h1, h2, h3, h4, h5, h6, p, li, dt, dd, blockquote, figcaption, th, td, caption'

// Navigation is the screen reader's job, and reading a table of contents
// before the article itself just delays the content the user came for.
const SKIP_INSIDE = '#a11y-toolbar, nav, aside, [role="navigation"], .menu, .breadcrumb, .pagination, .tabs, [aria-hidden="true"]'

export class Speech {
  static get supported() {
    return typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window
  }

  constructor({ onStateChange } = {}) {
    this.onStateChange = onStateChange || (() => {})
    this.speaking = false
    this.queue = []
    this.index = 0
    this.current = null // element being read
    this.voice = null

    if (!Speech.supported) return

    this.synth = window.speechSynthesis
    this.pickVoice()
    // Voices load asynchronously in most browsers.
    this.synth.addEventListener?.('voiceschanged', () => this.pickVoice())

    // Never let speech outlive the page.
    window.addEventListener('pagehide', () => this.stop())
  }

  pickVoice() {
    const voices = this.synth.getVoices() || []
    this.voice =
      voices.find((v) => /^ro/i.test(v.lang)) ||
      voices.find((v) => /^ro/i.test(v.name)) ||
      null
  }

  toggle(scope) {
    if (this.speaking) {
      this.stop()
    } else {
      this.start(scope)
    }
  }

  start(scope) {
    if (!Speech.supported) return

    this.queue = this.collect(scope || document.querySelector('main') || document.body)
    if (!this.queue.length) return

    // A pending queue from a previous run confuses some engines.
    this.synth.cancel()
    this.index = 0
    this.speaking = true
    this.onStateChange(true)
    this.speakNext()
    this.startWatchdog()
  }

  stop() {
    if (!Speech.supported) return
    this.speaking = false
    this.stopWatchdog()
    this.synth.cancel()
    this.highlight(null)
    this.onStateChange(false)
  }

  /**
   * Browsers occasionally drop the utterance queue without firing `onend`,
   * which would strand the button on "Oprește citirea" forever. Poll for a
   * synth that has gone quiet while we still believe we're reading.
   */
  startWatchdog() {
    this.stopWatchdog()
    let idleTicks = 0
    this.watchdog = setInterval(() => {
      if (!this.speaking) return this.stopWatchdog()
      if (this.synth.speaking || this.synth.pending) {
        idleTicks = 0
        return
      }
      // Two consecutive quiet ticks means nothing is coming back.
      if (++idleTicks >= 2) this.stop()
    }, 1000)
  }

  stopWatchdog() {
    if (this.watchdog) {
      clearInterval(this.watchdog)
      this.watchdog = null
    }
  }

  speakNext() {
    if (!this.speaking) return

    if (this.index >= this.queue.length) {
      this.stop()
      return
    }

    const { text, el } = this.queue[this.index++]
    const u = new SpeechSynthesisUtterance(text)
    u.lang = 'ro-RO'
    if (this.voice) u.voice = this.voice
    u.rate = 1
    u.onstart = () => this.highlight(el)
    u.onend = () => this.speakNext()
    // Don't get stuck on a chunk the engine refuses to speak.
    u.onerror = () => this.speakNext()
    this.synth.speak(u)
  }

  highlight(el) {
    if (this.current) this.current.classList.remove('a11y-reading')
    this.current = el
    if (!el) return
    el.classList.add('a11y-reading')

    const r = el.getBoundingClientRect()
    if (r.top < 0 || r.bottom > window.innerHeight) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  /** Flatten a container into [{text, el}] chunks in reading order. */
  collect(scope) {
    const out = []

    scope.querySelectorAll(READABLE).forEach((el) => {
      if (el.closest(SKIP_INSIDE)) return
      // Skips display:none, visibility:hidden and .is-sr-only alike.
      if (!el.getClientRects().length) return
      // Only leaf-ish blocks, so nested markup isn't read twice.
      if (el.querySelector(READABLE)) return

      const text = (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim()
      if (!text) return

      this.chunk(text).forEach((part) => out.push({ text: part, el }))
    })

    return out
  }

  chunk(text) {
    if (text.length <= MAX_CHUNK) return [text]

    const parts = []
    let buf = ''
    // Keep sentence punctuation with the sentence it ends.
    text.split(/(?<=[.!?…:;])\s+/).forEach((sentence) => {
      if ((buf + ' ' + sentence).trim().length > MAX_CHUNK && buf) {
        parts.push(buf.trim())
        buf = sentence
      } else {
        buf = (buf + ' ' + sentence).trim()
      }
    })
    if (buf) parts.push(buf.trim())

    // A single sentence can still exceed the limit — hard-split it.
    return parts.flatMap((p) =>
      p.length <= MAX_CHUNK ? [p] : (p.match(new RegExp(`.{1,${MAX_CHUNK}}(\\s|$)`, 'g')) || [p]).map((s) => s.trim())
    )
  }
}
